import React from "react";
import styled from "styled-components";

import { MenuItem } from "@blueprintjs/core";
import CountResult from "common/entities/CountResult";
import { CourseWrapper } from "common/entities/courseWrapper/CourseWrapper";
import { Completion } from "common/entities/Subscription";
import { FlexJustify } from "common/fragments/Flex";
import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import VerticalCenter from "common/fragments/VerticalCenter";
import { IPageableListQuery } from "common/models/IPageableListQuery";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { CardField, CardVariant, CardView } from "common/views/card/Card.view";
import { DropdownSelectVariant, DropdownSelectView } from "common/views/dropdownSelect/DropdownSelect.view";
import { OptionDropdownSelect } from "common/views/dropdownSelect/OptionDropdownSelect";
import HeadingView, { HeadingVariant } from "common/views/heading/Heading.view";
import IconView from "common/views/icon/Icon.view";
import { ImageVariant, ImageView } from "common/views/image/Image.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import PagingView from "common/views/paging/Paging.view";
import ProgressBarView from "common/views/progressBar/ProgressBar.view";
import { RowsView } from "common/views/rows/Rows.view";
import Query from "core/api/QueryComponent";
import Label from "core/components/label/Label";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { addEnum, Field } from "typegraph";
import { MetaProvider } from "./component/SubscriptionComponent";
import { Subscription } from "./entities/Subscription";
import labelProvider from './Subscriptions.labels';
import { sortOptions, SubscriptionsConfig } from "./SubscriptionsConfig";
import TypographyView from "common/views/typographyView/Typography.view";
import { getProgressTextLabel } from "common/fragments/ProgressText";
import { SeparatorView } from "common/views/separator/Separator.view";
import { WrapperDivView } from "common/views/wrapperDiv/WrapperDiv.view";
import Course from "../../panels/catalog/entities/Course";
import ExtendSubscription from "../../panels/catalog/fragments/course/extendSubscription";
import * as catalogActions from "../../panels/catalog/catalogActions";

/**
 * @overview: shows users subscriptions in a card-thumbnail or card-icon view, wrapped in a rows-container or a carousel-container.
 *
 * @author: jesposito, chills
 */



function mapParamsForCourse(data) {
	const params = {...data}; //clone

	//remove fields which are not using in query
	delete params['loadPrograms'];

	return {params: params};
}
class CourseQuery {
	@Field({
		entity: Course,
		params: mapParamsForCourse,
		aliasFor: "course_one"
	})
	public course: Course;
}

interface ISubscriptionsParams {
	completions?: any[];
	pageSize?: number;
	sort?: string;
}

const { Labels } = labelProvider;
const StyledThumbnailOverlay = styled(VerticalCenter)`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	transition: 0.1s;
	opacity: 0;
	color: #fff;
	cursor: pointer;
	&:hover {
		background-color: rgba(0,0,0,0.4);
		opacity: 1;
	}
`;

function mapParams(params: any) {
	return { params };
}

class SubscriptionsQuery implements IPageableListQuery<Subscription> {

	@Field({
		entity: Subscription,
		aliasFor: "subscriptionsUser",
		params: mapParams
	})
	public entities: null | Subscription[];

	@Field({
		entity: CountResult,
		aliasFor: "subscriptionsUserCount",
		params: mapParams
	})
	public count: CountResult;

}

interface ISubscriptionsPanelState {
	pageNumber: number;
	pageSize: number;
	sort: string;
	sortDesc: boolean;
	filter: 'all' | 'inprogress' | 'completed' | '';
}

const completionOptions = [
	{value: 'all', label: "All"},
	{value: 'inprogress', label: "In Progress"},
	{value: 'completed', label: "Completed"}
];

function getInitialState(config: SubscriptionsConfig): ISubscriptionsPanelState {

	return {
		pageSize: config.pageSize || 10,
		pageNumber: 0,
		sort: config.subscriptionParams['sort'] || 'lastVisited',
		sortDesc: typeof config.subscriptionParams['sortDesc'] === 'boolean' ? config.subscriptionParams['sortDesc'] : true,
		filter: config.subscriptionParams['completions'] || 'all',
	};
}

@Panel({id: "subscriptions", configClass: SubscriptionsConfig, labelProvider})
export class SubscriptionsPanel extends React.PureComponent<IPanelProps<SubscriptionsConfig>, ISubscriptionsPanelState> {

	public constructor(props: IPanelProps<SubscriptionsConfig>) {
		super(props);
		this.state = getInitialState(props.config);
	}


	private checkSubscriptionDefault() {
		const subscription = this.props.config.subscriptionParams;
		if (subscription &&  subscription["completions"] && subscription["completions"].length === 3) {
	        /**Completed check default */
			if (subscription["completions"].indexOf("NotStarted") > -1 && subscription["completions"].indexOf("Started") > -1 && subscription["completions"].indexOf("Incomplete") > -1) {
				this.setState({ pageNumber: 0, filter: "inprogress" })
			}else if (subscription["completions"].indexOf("Completed") > -1 && subscription["completions"].indexOf("Passed") > -1 && subscription["completions"].indexOf("Failed") > -1) {
				this.setState({ pageNumber: 0, filter: "completed" })
			}
		} else {
			this.setState({pageNumber: 0, filter:subscription['completions'] || "all"})
		}
	}



	componentDidMount(): void {
		this.checkSubscriptionDefault();
	}

	componentDidUpdate(prevProps: Readonly<IPanelProps<SubscriptionsConfig, any>>, prevState: Readonly<ISubscriptionsPanelState>, snapshot?: any): void {
		if (JSON.stringify(prevProps.config.subscriptionParams) != JSON.stringify(this.props.config.subscriptionParams)) {
			this.checkSubscriptionDefault();
		}
	}

	public render() {
		const { config } = this.props;
		const {subscriptionParams} = config;

		const pageSizeOptions: any = config.pageSizeOptions ? config.pageSizeOptions.split(',').map(Number).reduce((acc, i) => i ? [...acc, i] : acc, [config.pageSize]):[];
		const filteredPageOptions = pageSizeOptions.filter((item,
			index) => pageSizeOptions. indexOf(item) === index);
		const params = {
			pageNumber: this.state.pageNumber,
			pageSize: this.state.pageSize
		};

		Object.entries(config.subscriptionParams || {}).reduce((a, [k, v]) => {
			a[k] = v;
			return a;
		}, params);

		params['sort'] = (this.state.sortDesc ? '-' : '') + this.state.sort;
	
		delete params['sortDesc']; // removed from query params
		

		if ("inprogress" === this.state.filter) {
			params['completions'] = [addEnum(Completion.NotStarted), addEnum(Completion.Started), addEnum(Completion.Incomplete)];
		}

		if ("completed" === this.state.filter) {
			params['completions'] = [addEnum(Completion.Completed), addEnum(Completion.Passed), addEnum(Completion.Failed)];
		}

		if ("all" === this.state.filter) {
			delete params['completions']; 
		}
		

		return [
			config.enableRefine && (
				<BlockView key="refine" variant={BlockVariant.REFINE} viewKey="refine">
					<FlexJustify>
						<OptionDropdownSelect
							key="refine.completion"
							onChange={this.handleFilterChange}
							options={completionOptions}
							value={this.state.filter || subscriptionParams['completions']}
							variant={DropdownSelectVariant.MINIMAL}
							viewKey="refine-completion"
						>
							<Labels.Completion />
						</OptionDropdownSelect>
						<OptionDropdownSelect
							key="refine.sort"
							onChange={this.handleSort}
							options={sortOptions}
							value={this.state.sort || subscriptionParams['sort'] || 'lastVisited'}
							variant={DropdownSelectVariant.MINIMAL}
							viewKey="refine-sort"
						>
							<Labels.Sort />
						</OptionDropdownSelect>
					</FlexJustify>
				</BlockView>
			),
			<Query.Root key="query" entity={SubscriptionsQuery} args={params}>
				{({data, loading}) => {

					if (loading) {
						return <Loading key="loading" />;
					}

					if (!data || !data.entities || !data.entities.length) {
						return (
							<NonIdealState
								key="empty"
								title={<Labels.SubscriptionEmpty />}
								description={<Labels.SubscriptionEmptyDescription />}
							/>
						);
					}

					return (
						<div key="data">
							<RowsView viewKey="rows">
								{data.entities.map(this.renderSubscription)}
							</RowsView>
							<BlockView viewKey="bottom">
								<FlexJustify>
									<div>
										<PagingView
											viewKey="paging"
											pageNumber={this.state.pageNumber}
											pageSize={this.state.pageSize}
											total={data.count && data.count.count || 0}
											setPageNumber={this.handlePageNumberChange}
										/>
									</div>
									{
										!!filteredPageOptions.length && (
											<DropdownSelectView
												viewKey="pageSize"
												onChange={this.handlePageSizeChange}
												items={filteredPageOptions}
												itemRenderer={(size: number, { handleSelect }) => {
													return (
														<MenuItem
															key={size}
															text={<Label k="pageSize" v="Show ${size}" args={{size}} />}
															onClick={handleSelect}
														/>
													);
												}}
											>
												<Label k="pageSize" v="Show ${size}" args={{size: params['pageSize']}} />
											</DropdownSelectView>
										)
									}
									
								</FlexJustify>
							</BlockView>
						</div>
					);
				}}
			</Query.Root>
		];
	}

	private renderSubscription = (subscription: Subscription) => {
		const { config } = this.props;

		const w = CourseWrapper.forSubscription(subscription);

		const { subscriptionComponent = {} } = config;

		if (!w) {
			return null;
		}

		const params = {}
		if (subscription && (subscription.entry || subscription.product)) {
			params["id"] = subscription.entry.id;
		}

		const content = [
			config.showThumbnail && (
				<div key="thumbnail" style={{position: 'relative'}}>
					<ImageView
						key="thumbnail"
						src={w.getIconUrl()}
						style={{
							minHeight: config.inlineLayout ? '100%' : config.thumbnailSize,
							width: config.inlineLayout ? (config.thumbnailSize) : '100%'
						}}
						variant={ImageVariant.THUMBNAIL}
						viewKey="thumbnail"
					/>
					{
						w.isLaunchable() && (
							<StyledThumbnailOverlay onClick={() => w.launchCourse()}>
								<IconView icon="play" viewKey="launch-icon" />
							</StyledThumbnailOverlay>
						)
					}
				</div>

			),
			(
				<BlockView key="primary" variant={BlockVariant.CARD_CONTENT} style={{flexGrow: 1}} viewKey="course-content">
					<WrapperDivView style={{minHeight:85}} viewKey="subscription.title.wrapper">
						<HeadingView style={{height:70}} viewKey="title" children={w.getTitle()} />
					</WrapperDivView>
					{
						config.showSubscriptionMeta && (
							<CardField style={{height:85}}>
								<MetaProvider
									fragments={subscriptionComponent.meta}
									fragmentProps={{config: subscriptionComponent, subscription}}
								/>
							</CardField>
						)
					}
					{
						config.showProgress && (
							<CardField style={{height:24}}>
								{/* <ProgressBarView value={w.getProgressPercent()} viewKey="course-progress" /> */}
								{
									subscription && subscription.product && subscription.product.type.baseType === "base:package" ?
										<React.Fragment>
											<TypographyView style={{textAlign:"center"}} viewKey="subscription.progressPercent">{parseInt(subscription.completionPercentage)+"%"}</TypographyView>
											<ProgressBarView viewKey="course-progress" value={Number(subscription.completionPercentage)/100} />
										</React.Fragment>
										:
										<ProgressBarView value={w.getProgressPercent()} viewKey="course-progress" />
								}
							</CardField>
						)
					}
				</BlockView>
			),
			(
				<BlockView key="subscriptions-progress-text" variant={BlockVariant.CARD_CONTENT} style={{flexGrow: 1}} viewKey="course-progress-text">
					{ 	
						config.showProgressText && (
							<CardField style={{height:24}}>
								<TypographyView viewKey="subscription.progressText">
									{getProgressTextLabel(subscription && subscription.completion)}
								</TypographyView>
							</CardField>
						)
					}
				</BlockView>
			),
			(
				<React.Fragment>
					{
						subscription && subscription.entry && <Query.Root entity={CourseQuery} args={params}>
						{
							({ data, loading }) => {

								if (loading) {
									return (
										<React.Fragment></React.Fragment>
									);
								}
								const course = data && data.course;
								const cond = course && course.subscriptionEndDate && !course.canExtend && !course.activeSubscriptionExists;
								return <React.Fragment>
									<BlockView key="subscriptions-expire-text" variant={BlockVariant.CARD_CONTENT} style={{flexGrow: 1}} viewKey="course-expire-text">
										<CardField style={{height:24}}>
											{config.subscriptionExpired && cond && (
											
												<TypographyView viewKey="subscriptionexpire.title">
												Subscription is Expired
												</TypographyView>
												
												
											)}
										</CardField>
									</BlockView>

									<BlockView style={{height:70}} key="actions" variant={BlockVariant.CARD_ACTIONS} viewKey="course-actions">
										{
											w.isLaunchable() && !cond &&(
												<ButtonView variant={ButtonVariant.CARD_ACTION} onClick={() => w.launchCourse()} viewKey="course-button">
													<Labels.CourseLaunch />
												</ButtonView>
											)
										}
										{
											config.extendSubscription && course && course.canExtend && course.canExtend != undefined &&
											<React.Fragment>
													<ExtendSubscription course={course} actions={catalogActions} disableDirectBuy={true} />
													&nbsp;&nbsp;
											</React.Fragment> 
											
										}
										{
											config.enableViewDetails && (
												<PageLinkView params={{entryId: w.getEntryId()}} viewKey="course-view-link">
													<ButtonView variant={ButtonVariant.CARD_ACTION} viewKey="course-details-button">
														<Labels.CourseView />
													</ButtonView>
												</PageLinkView>
											)
										}

									</BlockView>
								</React.Fragment>

							}
						}
					</Query.Root>
					}
				</React.Fragment>
			),
			// (
			// 	<BlockView key="actions" variant={BlockVariant.CARD_ACTIONS} viewKey="course-actions">
			// 		{
			// 			w.isLaunchable() && !w.isSubscriptionExpired() &&(
			// 				<ButtonView variant={ButtonVariant.CARD_ACTION} onClick={() => w.launchCourse()} viewKey="course-button">
			// 					<Labels.CourseLaunch />
			// 				</ButtonView>
			// 			)
			// 		}
			// 		{
			// 			config.enableViewDetails && (
			// 				<PageLinkView params={{entryId: w.getEntryId()}} viewKey="course-view-link">
			// 					<ButtonView variant={ButtonVariant.CARD_ACTION} viewKey="course-details-button">
			// 						<Labels.CourseView />
			// 					</ButtonView>
			// 				</PageLinkView>
			// 			)
			// 		}
			// 	</BlockView>
			// )
		];

		return (
			<CardView variant={CardVariant.SURFACE} viewKey="card">
				{
					config.inlineLayout ? (
						<div style={{display: 'flex', flexDirection: "row"}}>
							{content}
						</div>
					) : content
				}
			</CardView>
		);
	}

	private handleSort = (sort: string) => {
		const {subscriptionParams} = this.props.config;
		if (sort === this.state.sort) {
			this.setState({sort, sortDesc: !this.state.sortDesc});
		} else {
			this.setState({sort, sortDesc: typeof subscriptionParams['sortDesc'] === 'boolean' ? subscriptionParams['sortDesc'] : false});
		}
	}

	private handlePageSizeChange = (pageSize: number) => this.setState({pageNumber: 0, pageSize});

	private handlePageNumberChange = (pageNumber: number) => this.setState({pageNumber});

	private handleFilterChange = (filter: 'all' | 'inprogress' | 'completed') => this.setState({pageNumber: 0, filter});
}



// WEBPACK FOOTER //
// ./src/panels/subscriptions/Subscriptions.panel.tsx